import classNames from 'clsx';
import React from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { PriorityImage } from '@swf/poker';

import RegularBackground from '../../../assets/tiles/spingo-combined.svg';
import { usePokerSpinAndGoWidget } from '../../../model';
import { AssetType } from '../../../types/';
import type { GameTypeName, GameTypeUrl } from '../../../types/Game';
import { isTilePromo } from '../../../utils';
import { getBackgroundClasses } from '../../../utils/assets';
import styles from '../shared.scss';

import { TXTMOB_Win_up_to_N0X } from './Background.messages.json';
import PromoBackground from './PromoBackground';

export interface BackgroundProps {
  winUpTo: string;
  gameType: GameTypeName;
  isUltra: boolean;
  url: GameTypeUrl;
  promoRef: string;
  scriptId: number;
  widgetId?: string;
}

interface BackgroundContentProps {
  gameType: GameTypeName;
  promoRef: string;
  winUpTo: string;
  backgroundAssetUrl: string | null;
  isPromo: boolean;
  formattedAltText: string;
}

const BackgroundContent: React.FC<BackgroundContentProps> = ({
  gameType,
  promoRef,
  winUpTo,
  backgroundAssetUrl,
  isPromo,
  formattedAltText
}) => {
  if (isPromo) {
    return <PromoBackground promoRef={promoRef} gameType={gameType} winUpTo={winUpTo} />;
  }

  if (backgroundAssetUrl) {
    return (
      <PriorityImage
        imageUrl={backgroundAssetUrl}
        fetchPriority="high"
        altText={`${gameType} ${formattedAltText}`}
      />
    );
  }

  return <RegularBackground />;
};

const Background: React.FC<BackgroundProps> = ({
  winUpTo,
  gameType,
  isUltra,
  url,
  promoRef,
  scriptId,
  widgetId
}) => {
  const { formatMessage } = useIntl();
  const { widgetSelect } = usePokerSpinAndGoWidget(widgetId);
  const backgroundAssetUrl = useSelector((state: RootState) =>
    widgetSelect.getActiveAssetConfig(state, gameType, AssetType.Tile)
  );
  const { url: tileBackground, alt: tileAltText } = useSelector(
    (state: RootState) => widgetSelect.getActiveTileBackground(state, gameType, scriptId),
    shallowEqual
  );
  const isPromo = !tileBackground && isTilePromo(promoRef, isUltra);

  const backgroundClasses = getBackgroundClasses(url, gameType);
  const formattedAltText = formatMessage(TXTMOB_Win_up_to_N0X, { 0: winUpTo });

  return (
    <picture data-ot-ignore className={classNames(styles.bgr, backgroundClasses)}>
      <BackgroundContent
        gameType={gameType}
        promoRef={promoRef}
        winUpTo={winUpTo}
        backgroundAssetUrl={tileBackground || backgroundAssetUrl}
        isPromo={isPromo}
        formattedAltText={tileAltText || formattedAltText}
      />
    </picture>
  );
};

export default Background;
